/* ────────────────────────────────────────────────────────────────────────
 * Inventory availability — decoupled business logic
 *
 * Pure module: takes a raw `integration_data` payload in, returns a fully
 * resolved `AvailabilityView` out. No React, no i18n, no DOM — so the cart's
 * stock/price rendering is a thin projection of this, and every rule below is
 * unit-testable in isolation.
 *
 * The rules implement the "Inventory Logic" spec (Inventory & Prices.pdf). The
 * system evaluates up to two inventory SOURCES to decide one of three statuses —
 * In-Stock, Out-of-Stock, or Stock Unknown:
 *
 *   P1 · Retailer (dms_data)
 *     - source null            → Stock Unknown ("No DMS integration exists")
 *     - empty results ([])     → Stock Unknown ("part not found")
 *     - 1+ rows                → Original/Alternative dropdown; In-Stock /
 *                                Not-in-Stock by quantity; per-location breakdown.
 *   P2 · Importer (importer_data; legacy importer_inventory / importer_price)
 *     - source null            → Stock Unknown ("No importer integration"; CTA to connect)
 *     - empty results ([])     → Stock Unknown ("Not found in ERP")
 *     - 1+ rows                → In-Stock if any row qty > 0, else Out-of-Stock.
 *
 *   Combined: if there is stock ANYWHERE (retailer OR importer) the status is
 *   In-Stock, and the detail breakdown lists every available location including
 *   the importer. Out-of-Stock requires a definitive zero and NO unresolved
 *   ("not found" / unknown-qty) source. A source that is simply not integrated
 *   for the org is ignored rather than treated as a zero.
 *
 * The integration payload also carries a `time` field (ISO datetime): when the
 * check was performed. It surfaces as `checkedAt` so the UI can show "checked N
 * minutes ago" and offer a re-check.
 * ──────────────────────────────────────────────────────────────────────── */

export type StockStatus = 'in_stock' | 'unknown' | 'out_of_stock';

/** Which data drives the display, resolved per part:
 *   - 'dms'      → retailer DMS present: show it (grouped by type, qty numbers).
 *   - 'importer' → no DMS but importer present: show price + in/out (no number).
 *   - 'none'     → neither → Unknown. */
export type AvailSource = 'dms' | 'importer' | 'none';

/** Per-source presence, distinguishing the spec's null vs empty-results cases:
 *   - 'missing' → source key absent/null: the integration does not exist for the org.
 *   - 'empty'   → integration ran but returned no rows ([]): part not found.
 *   - 'present' → 1+ usable rows. */
export type SourcePresence = 'missing' | 'empty' | 'present';

export interface AvailLocation {
  label: string;          // warehouse display name: location_alias, else location key ('' when none)
  qty: number | null;     // null → unknown
  /** Stable id for the warehouse / point of sale (the DMS `location` key, else
   *  the label) — what a per-line location choice is persisted as. */
  key: string;
  /** This warehouse's OWN price. The same part can be priced differently per
   *  point of sale, so a location choice must apply its price, not the group's. */
  price: number | null;
}

/** A predefined delivery option (spec §6) — a tenant-configured zone with its
 *  day count, shown in the per-line delivery dropdown. */
export interface DeliveryOption {
  label: string;   // zone label, e.g. "Santiago" / "Outside" / "International"
  days: number;
}

/** One DMS line-type (e.g. 'original' / 'alternative'), with its shared price and the
 *  per-location stock (usually one price across a type's locations). */
export interface DmsTypeGroup {
  type: string;
  price: number | null;
  totalQty: number | null;      // sum of known location qtys; null when all unknown
  locations: AvailLocation[];
  sku?: string | null;          // this line-type's supplier SKU (switches with the type)
  /** Key of the warehouse that prices this line-type by default (org location
   *  policy + member home warehouse, see pickDefaultLocation); null when the
   *  type has no named locations. `price` is that warehouse's price. */
  defaultKey?: string | null;
  /** Why `defaultKey` was chosen: 'home' (member's home warehouse has stock),
   *  'lowest' (cheapest in-stock warehouse), 'first' (first in-stock warehouse in
   *  list order), 'fallback' (nothing in stock — a priced row kept for the price). */
  defaultReason?: DefaultLocationReason | null;
  /** Every warehouse key that counts as "home" for the member (all warehouses in
   *  their home city), trimmed; empty when none is configured. */
  homeKeys?: string[];
  /** The member's home city label (for "Not in {home}"), if any. */
  homeLabel?: string | null;
  /** How the home warehouses stand for this line-type: null when no home is
   *  configured; 'in_stock' when any has stock; 'out_of_stock' when rows exist
   *  but none has stock; 'absent' when the DMS returned no row for any of them. */
  homeStatus?: HomeStatus | null;
}
export type DefaultLocationReason = 'home' | 'lowest' | 'first' | 'fallback';
export type HomeStatus = 'in_stock' | 'out_of_stock' | 'absent';

/** How the resolved status is qualified in the chip (a remark never changes the
 *  status colour):
 *   - 'warehouse'    → In Stock via retailer; remark is the winning warehouse.
 *   - 'importer'     → In Stock via importer (retailer had none).
 *   - 'retailer_oos' → Unknown: retailer confirmed 0, importer unresolved. */
export type StockRemark = 'warehouse' | 'importer' | 'retailer_oos' | null;

export interface AvailabilityView {
  source: AvailSource;
  status: StockStatus;
  remarkKind: StockRemark;          // qualifies the status
  remarkWarehouse: string | null;    // winning warehouse name when remarkKind === 'warehouse'
  supplierSku: string | null;       // blue supplier/DMS SKU shown before the pill
  price: number | null;             // PRIMARY price → UNIT/TOTAL (new DMS, else importer)
  dmsGroups: DmsTypeGroup[];         // populated when source === 'dms'
  importerPrice: number | null;      // populated when source === 'importer'
  importerInStock: boolean | null;   // true/false; null = importer present but no qty
  importerQty: number | null;        // importer country-level qty (null = no data)
  importerLocations: AvailLocation[]; // per-warehouse importer stock (empty = no warehouse breakdown)
  importerFieldPresent: boolean;     // importer_data (or legacy importer_inventory / importer_price) key present at all
  dmsPresence: SourcePresence;      // spec P1: missing (no integration) / empty (not found) / present
  importerPresence: SourcePresence; // spec P2: missing (no integration) / empty (not found) / present
  selectedType: string | null;       // which DMS line-type drives `price` (null for importer/none)
  checkedAt: string | null;          // ISO datetime the check ran (integration_data.time), or null
}

/** Org-configurable availability display (admin → Organization Settings):
 *  which source wins when both are present, and whether to show the DMS
 *  "alternative" line-type. */
export type AvailPriority = 'dms_first' | 'importer_first' | 'dms_only' | 'importer_only';
/** Which DMS price line-type shows (and drives the effective price) BY DEFAULT
 *  when both "original" and "alternative" exist. The end user can still switch
 *  per line in the cart. */
export type PricePriority = 'original_first' | 'alternative_first' | 'lowest_available_first';
/** Which WAREHOUSE prices a line by default when a line-type sits in several
 *  (org setting `inventory_location_priority`):
 *   - 'list_order'       → (default) the first priced row the DMS returned, with
 *                          the chip showing the line-type's national total — the
 *                          behaviour every org had before warehouse policies;
 *   - 'home_then_lowest' → the member's home warehouse when it has stock, else the
 *                          lowest in-stock price;
 *   - 'home_then_first'  → home when it has stock, else the first in-stock
 *                          warehouse in list order;
 *   - 'lowest'           → always the lowest in-stock price.
 *  In every policy an in-stock warehouse beats an out-of-stock one, and a priced
 *  one beats an unpriced one; the user can still pick any warehouse per line. */
export type LocationPriority = 'list_order' | 'home_then_lowest' | 'home_then_first' | 'lowest';
export interface AvailConfig {
  priority: AvailPriority;
  showAlternative: boolean;
  pricePriority: PricePriority;
  /** Default-warehouse policy; absent → 'list_order' (legacy). */
  locationPriority?: LocationPriority;
  /** The current member's home warehouse codes (`location` keys) — every
   *  warehouse in their home city. A single string is accepted for callers that
   *  still pass one code. */
  homeLocation?: string | string[] | null;
  /** The member's home city label, shown in "Not in {home}". */
  homeLabel?: string | null;
}
export const DEFAULT_AVAIL_CONFIG: AvailConfig = { priority: 'dms_first', showAlternative: true, pricePriority: 'original_first', locationPriority: 'list_order', homeLocation: null, homeLabel: null };

/** Normalise `homeLocation` (string | string[] | null) to trimmed, non-empty keys. */
export const homeKeysOf = (home: string | string[] | null | undefined): string[] =>
  (Array.isArray(home) ? home : home ? [home] : []).map(h => String(h).trim()).filter(Boolean);

/** Recover the REAL amount from a dot-format ('CL/EU': 1.000 = one thousand)
 *  price value. Sources are inconsistent: some DMS feeds deliver the dot-thousands
 *  string misparsed as a decimal (7.817 arrives as the float 7.817 but means
 *  7,817), while others (and the importer) deliver the full real amount (693975).
 *  A dot-format price has no legitimate fractional part (dot groups thousands,
 *  cents are unused), so a fractional value IS the misparse — recover with ×1000.
 *  An integer value is already real — NEVER scale it (the old blanket ×1000
 *  turned 693975 into 693,975,000). The epsilon absorbs float noise; ×1000
 *  results are rounded to 2 decimals for the same reason (7.817 → 7817). */
export const toRealDotAmount = (num: number): number => {
  const rounded = Math.round(num);
  if (Math.abs(num - rounded) < 1e-9) return rounded;
  return Math.round(num * 1000 * 100) / 100;
};

/** Parse price to number safely, returns null if not parseable.
 *  With numberFormat 'dot', the result is normalized to the REAL amount:
 *  dot-grouped strings are read as thousands ("693.975" → 693975, "13.602,5" →
 *  13602.5) and already-misparsed numeric values are recovered via
 *  toRealDotAmount. Default ('comma') behavior is unchanged. */
export const parsePrice = (price: string | number | undefined | null, numberFormat: string = 'comma'): number | null => {
  if (price === null || price === undefined) return null;
  if (typeof price === 'number') {
    if (isNaN(price)) return null;
    return numberFormat === 'dot' ? toRealDotAmount(price) : price;
  }
  const str = String(price);
  if (!str || str === '--' || str === 'undefined' || str === 'N/A') return null;
  const cleaned = str.replace(/[^0-9.,]/g, '');
  if (numberFormat === 'dot' && /^\d{1,3}(\.\d{3})+(,\d+)?$/.test(cleaned)) {
    // Unambiguous dot-thousands grouping (comma decimal) — already real.
    const num = parseFloat(cleaned.replace(/\./g, '').replace(',', '.'));
    return isNaN(num) ? null : num;
  }
  const num = parseFloat(cleaned.replace(',', '.'));
  if (isNaN(num)) return null;
  return numberFormat === 'dot' ? toRealDotAmount(num) : num;
};

/** Parse a possibly-stringy integer quantity, null when not parseable. */
export const parseQty = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return isNaN(v) ? null : v;
  const n = parseInt(String(v).replace(/[^0-9-]/g, ''), 10);
  return isNaN(n) ? null : n;
};

/** A DMS row's line-type, normalized to the THREE canonical buckets the cart
 *  exposes: 'genuine' (the automaker's OE part), 'oem' (original-equipment
 *  manufacturer, unbranded) and 'aftermarket' (third-party replacement).
 *
 *  The raw `type` is org-data-driven — a DMS/pricing-workflow write-back with NO
 *  fixed enum — so we recognize the common tokens and fall back sensibly:
 *    • genuine / oe / oes / original / new → 'genuine'
 *    • oem                                 → 'oem'
 *    • aftermarket / am / aft / alternative / alt → 'aftermarket'
 *    • any other non-empty value           → 'aftermarket' (was 'alternative')
 *    • absent                              → 'genuine'    (was 'original')
 *  Non-letters are stripped first so "after-market", "O.E.M." etc. still match. */
export const dmsRowType = (e: any): string => {
  if (e?.type) {
    const ty = String(e.type).toLowerCase().replace(/[^a-z]/g, '');
    if (ty === 'oem') return 'oem';
    if (ty === 'genuine' || ty === 'oe' || ty === 'oes' || ty === 'original' || ty === 'new') return 'genuine';
    // aftermarket / am / aft / alternative / alt — and anything unrecognized.
    return 'aftermarket';
  }
  if (e?.oem) return 'oem';
  if (e?.alternative) return 'aftermarket';
  if (e?.original || e?.new || e?.genuine) return 'genuine';
  return 'genuine';
};

/** Read the integration_data payload off an item, tolerating both the top-level
 *  field and the nested structured_data.part_data location. */
export const getIntegrationData = (item: any): any | undefined => {
  if (item?.integration_data !== undefined) return item.integration_data;
  const pd = item?.structured_data?.part_data || item?.structured_data?.PART_DATA;
  return pd?.integration_data;
};

const _DMS_TYPE_ORDER = ['genuine', 'oem', 'aftermarket'];

/** Coerce a check timestamp — an ISO string, a numeric string, or a Unix epoch
 *  in seconds or milliseconds — to epoch milliseconds. Values below 1e12 are
 *  treated as seconds (1e12 ms ≈ year 2001, so any real ms value exceeds it).
 *  Returns null when unparseable. */
const _toEpochMs = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return isNaN(v) ? null : (v < 1e12 ? v * 1000 : v);
  const s = String(v).trim();
  if (/^\d+$/.test(s)) { const n = parseInt(s, 10); return isNaN(n) ? null : (n < 1e12 ? n * 1000 : n); }
  const t = Date.parse(s);
  return isNaN(t) ? null : t;
};

/** Normalize a check timestamp (accepts `time` or `datetime`, ISO or epoch) to an
 *  ISO string, or null. Storing ISO keeps `checkedAt` uniform for both display
 *  (new Date(...).toLocaleString()) and relative formatting. */
const normalizeCheckedAt = (v: unknown): string | null => {
  const ms = _toEpochMs(v);
  return ms === null ? null : new Date(ms).toISOString();
};

/** True when a DMS group has a known, positive total stock. */
export const groupInStock = (g: DmsTypeGroup): boolean => g.totalQty !== null && g.totalQty > 0;

/** The distinct warehouses / points of sale of a line-type a user can pick from,
 *  in DMS row order (so a member's "prioritize" visibility rule — home branch
 *  first — carries through). Rows sharing a key collapse into one entry: qtys are
 *  summed (null only when all are unknown) and the first known price is kept.
 *  Rows with no location at all are dropped — there is nothing to choose. */
export const locationOptions = (group: DmsTypeGroup | null | undefined): AvailLocation[] => {
  if (!group) return [];
  const out: AvailLocation[] = [];
  const byKey = new Map<string, AvailLocation>();
  for (const l of group.locations) {
    if (!l.key) continue;
    const prev = byKey.get(l.key);
    if (!prev) {
      const copy = { ...l };
      byKey.set(l.key, copy);
      out.push(copy);
      continue;
    }
    if (l.qty !== null) prev.qty = (prev.qty ?? 0) + l.qty;
    if (prev.price === null) prev.price = l.price;
  }
  return out;
};

/** Pick the warehouse that prices a line-type by default, per the org policy and
 *  the member's home warehouse (see LocationPriority). Pure; `opts` is the
 *  collapsed location list from locationOptions(). */
export const pickDefaultLocation = (
  opts: AvailLocation[],
  locationPriority: Exclude<LocationPriority, 'list_order'> = 'home_then_lowest',
  homeLocation?: string | string[] | null,
): AvailLocation | null => resolveDefaultLocation(opts, locationPriority, homeLocation).location;

const sameKey = (a: string, b: string) => a.trim().toUpperCase() === b.trim().toUpperCase();

/** pickDefaultLocation plus WHY, and how the home warehouses stand — what the cart
 *  shows next to the default ("home" vs "lowest price, none in your city").
 *  A home city can hold several warehouses: the cheapest in-stock one among them
 *  is the default (list order breaks a tie). */
export const resolveDefaultLocation = (
  opts: AvailLocation[],
  locationPriority: Exclude<LocationPriority, 'list_order'> = 'home_then_lowest',
  homeLocation?: string | string[] | null,
): { location: AvailLocation | null; reason: DefaultLocationReason | null; homeStatus: HomeStatus | null } => {
  const inStock = (o: AvailLocation) => (o.qty ?? 0) > 0;
  const priced = (o: AvailLocation) => o.price !== null;
  const cheapest = (arr: AvailLocation[]) =>
    arr.reduce((lo, o) => ((o.price ?? Infinity) < (lo.price ?? Infinity) ? o : lo));
  const homeKeys = homeKeysOf(homeLocation);
  const homes = opts.filter(o => homeKeys.some(h => sameKey(o.key, h)));
  const homeInStock = homes.filter(o => inStock(o) && priced(o));
  const homeStatus: HomeStatus | null = !homeKeys.length ? null : !homes.length ? 'absent' : homes.some(inStock) ? 'in_stock' : 'out_of_stock';
  if (!opts.length) return { location: null, reason: null, homeStatus };
  if (locationPriority !== 'lowest' && homeInStock.length) return { location: cheapest(homeInStock), reason: 'home', homeStatus };
  // Home has stock but no price on any in-stock row: still home (the price may come from elsewhere).
  const homeStocked = homes.find(inStock);
  if (locationPriority !== 'lowest' && homeStocked) return { location: homeStocked, reason: 'home', homeStatus };
  const available = opts.filter(o => inStock(o) && priced(o));
  if (available.length) {
    return locationPriority === 'home_then_first'
      ? { location: available[0], reason: 'first', homeStatus }
      : { location: cheapest(available), reason: 'lowest', homeStatus };
  }
  // Nothing in stock: keep a home price if one exists (cheapest), else the first priced row.
  const homePriced = homes.filter(priced);
  if (locationPriority !== 'lowest' && homePriced.length) return { location: cheapest(homePriced), reason: 'fallback', homeStatus };
  return { location: opts.find(priced) ?? opts[0], reason: 'fallback', homeStatus };
};

/** Whether a location is one of the member's home warehouses. */
export const isHomeLocation = (group: DmsTypeGroup | null | undefined, o: AvailLocation): boolean =>
  (group?.homeKeys ?? []).some(h => sameKey(o.key, h));

/** Location options in the order the advisor should read them: the home
 *  warehouses first (in stock, cheapest first, then the rest of home), then the
 *  other warehouses with stock cheapest-first, then the rest (out of stock, then
 *  unknown stock). Stable within each band. */
export const orderedLocationOptions = (group: DmsTypeGroup | null | undefined): AvailLocation[] => {
  const opts = locationOptions(group);
  const stocked = (o: AvailLocation) => (o.qty ?? 0) > 0;
  const band = (o: AvailLocation) =>
    isHomeLocation(group, o) ? (stocked(o) ? 0 : 1) : stocked(o) ? 2 : o.qty === null ? 4 : 3;
  const byPrice = (b: number) => b === 0 || b === 2;
  return opts
    .map((o, i) => ({ o, i, b: band(o) }))
    .sort((x, y) => x.b - y.b || (byPrice(x.b) ? (x.o.price ?? Infinity) - (y.o.price ?? Infinity) : 0) || x.i - y.i)
    .map(x => x.o);
};

/** The location the line is currently priced from: the user's explicit choice
 *  when it still exists in the group, else the group's default warehouse
 *  (pickDefaultLocation, resolved in deriveAvailability), else the first priced
 *  row, else the first location. */
export const activeLocation = (
  group: DmsTypeGroup | null | undefined,
  selectedKey?: string | null,
): AvailLocation | null => {
  const opts = locationOptions(group);
  if (!opts.length) return null;
  if (selectedKey) {
    const hit = opts.find(o => o.key === selectedKey);
    if (hit) return hit;
  }
  if (group?.defaultKey) {
    const def = opts.find(o => o.key === group.defaultKey);
    if (def) return def;
  }
  return opts.find(o => o.price !== null) ?? opts[0];
};

/** Unit price for a line priced from a given location — the location's own price,
 *  falling back to the line-type's shared price when that row carries none. */
export const priceForLocation = (group: DmsTypeGroup, loc: AvailLocation | null): number | null =>
  loc?.price ?? group.price;

/** The maximum quantity a user can order for a line's currently-selected
 *  line-type — used to warn when the ordered qty exceeds stock. This is the KNOWN
 *  retailer/DMS count only: the importer never exposes a trustworthy count (it
 *  reads "In Stock" without a number), so importer-only stock returns null. A
 *  null result means "unknown / no finite cap" — callers must not warn against it.
 *
 *  @param view         derived availability (null → fall back to legacyQty)
 *  @param selectedType per-line chosen DMS type (falls back to view.selectedType)
 *  @param legacyQty    old inventory_data[0] stock qty, used when view is null */
export const availableQtyForType = (
  view: AvailabilityView | null,
  selectedType: string | null | undefined,
  legacyQty: number | null,
  /** Explicitly chosen warehouse key — when set, the cap is THAT location's stock
   *  (the order ships from it), not the line-type's national total. */
  selectedLocation?: string | null,
): number | null => {
  if (!view) return legacyQty;
  if (view.source !== 'dms') return null;   // importer-only / none → no reliable count
  const activeType = selectedType ?? view.selectedType ?? null;
  const g = view.dmsGroups.find(gr => gr.type === activeType) ?? null;
  // Price and stock come from one warehouse: the explicit choice, else the
  // line-type's default warehouse. The national total only when neither exists.
  const key = selectedLocation || g?.defaultKey || null;
  if (g && key) {
    const loc = locationOptions(g).find(o => o.key === key);
    if (loc) return loc.qty;
  }
  return g ? g.totalQty : null;
};

/** The supplier/DMS SKU shown for a line's selected type — the active DMS group's
 *  SKU, falling back to the view-level supplier SKU (importer supersession / OE).
 *  This is exactly the blue SKU IntegrationAvailability renders; a value that
 *  DIFFERS from the requested OE number is the supersession/replacement part. */
export const supplierSkuForType = (
  view: AvailabilityView | null,
  selectedType?: string | null,
): string | null => {
  if (!view) return null;
  const activeType = selectedType ?? view.selectedType;
  const g = (view.source === 'dms' ? view.dmsGroups : []).find(gr => gr.type === activeType) ?? null;
  const sku = g?.sku ?? view.supplierSku;
  return sku != null && sku !== '' ? String(sku) : null;
};

/** Pick the DMS line-type group that drives the primary/default price, honoring
 *  the org's price-priority config and stock availability. Only ever returns a
 *  group that exists in `groups`. */
export const selectDmsGroup = (
  groups: DmsTypeGroup[],
  pricePriority: PricePriority,
  /** True when the importer (the OE source) is in stock — makes the OE
   *  ('genuine') group count as available for selection even when it carries no
   *  DMS stock of its own (its stock comes from the importer). */
  oeInStockViaImporter: boolean = false,
): DmsTypeGroup | null => {
  if (!groups.length) return null;
  const inStock = (g: DmsTypeGroup) => groupInStock(g) || (g.type === 'genuine' && oeInStockViaImporter);
  const priced = groups.filter(g => g.price !== null);
  const cheapest = (arr: DmsTypeGroup[]) =>
    arr.reduce((lo, g) => ((g.price ?? Infinity) < (lo.price ?? Infinity) ? g : lo));
  if (pricePriority === 'lowest_available_first') {
    const avail = priced.filter(inStock);
    if (avail.length) return cheapest(avail);
    if (priced.length) return cheapest(priced);
    return groups[0];
  }
  // The org priority is binary (genuine-first vs aftermarket-first); reinterpret
  // it across the three ordered buckets. Prefer the first in-stock bucket in
  // priority order, then the first that merely exists.
  const order = pricePriority === 'alternative_first'
    ? ['aftermarket', 'oem', 'genuine']
    : ['genuine', 'oem', 'aftermarket'];
  for (const ty of order) {
    const g = groups.find(x => x.type === ty);
    if (g && inStock(g)) return g;
  }
  for (const ty of order) {
    const g = groups.find(x => x.type === ty);
    if (g) return g;
  }
  return groups[0];
};

/** Inputs to the per-line-type chip status resolver. */
export interface ChipStatusInput {
  dmsPresence: SourcePresence;
  /** Total stock for the SELECTED DMS line-type: >0 in stock, 0/null no rows. */
  activeQty: number | null;
  importerPresence: SourcePresence;
  /** Importer country-level stock: true in, false zero, null present-but-unknown-qty. */
  importerInStock: boolean | null;
}

export interface ChipStatus { status: StockStatus; remarkKind: StockRemark; }

/** Resolve the chip status for the currently-selected line-type (first match wins):
 *   1. selected retailer type has stock          → In Stock · warehouse
 *   2. importer (integrated, ≥1 row) has stock    → In Stock · importer
 *   3. importer might still stock it (qty unknown) → Unknown (retailer_oos if
 *                                                    retailer confirmed zero)
 *   4. any DEFINITIVE "no stock" signal           → Out of Stock
 *   5. otherwise                                  → Unknown
 *
 *  Presence & signal semantics:
 *   - 'missing'  source is not integrated for the org → ignored entirely.
 *   - A DEFINITIVE no-stock ("hard zero") is: a retailer that returned rows but
 *     zero for the selected type; an importer zero-qty row; OR an importer whose
 *     ERP was queried and did NOT find the part ('empty' [] → "Not found in ERP").
 *     An ERP not-found is authoritative, so it forces Out-of-Stock even when the
 *     retailer only returned [] (see below).
 *   - A retailer 'empty' ([]) on its OWN is INCONCLUSIVE (the DMS just didn't
 *     recognise the part number) → Unknown; but it does not BLOCK an Out-of-Stock
 *     decision that a hard zero (e.g. importer ERP not-found) already justifies.
 *   - An importer that is integrated with a row but UNKNOWN qty "might still have
 *     stock" → Unknown, and this DOES block Out-of-Stock. */
export const resolveChipStatus = (input: ChipStatusInput): ChipStatus => {
  const { dmsPresence, activeQty, importerPresence, importerInStock } = input;

  // 1 — selected retailer line-type has stock.
  if (activeQty !== null && activeQty > 0) return { status: 'in_stock', remarkKind: 'warehouse' };
  // 2 — importer has stock (only counts when integrated with usable rows).
  if (importerPresence === 'present' && importerInStock === true) return { status: 'in_stock', remarkKind: 'importer' };

  // Retailer confirmed zero: rows returned, but the selected type is 0 / absent.
  const retailerZero = dmsPresence === 'present';
  // Definitive importer negative: a zero-qty row, or an ERP not-found ([]).
  const importerHardZero = (importerPresence === 'present' && importerInStock === false)
    || importerPresence === 'empty';
  const hardZero = retailerZero || importerHardZero;
  // Importer integrated, row present, qty unknown → it MIGHT still stock the part.
  const importerMaybe = importerPresence === 'present' && importerInStock === null;

  // 3 — an importer that might still have it keeps us from declaring Out-of-Stock.
  if (importerMaybe) return { status: 'unknown', remarkKind: retailerZero ? 'retailer_oos' : null };
  // 4 — a definitive no-stock signal → Out of Stock.
  if (hardZero) return { status: 'out_of_stock', remarkKind: null };
  // 5 — a lone inconclusive retailer ([]) or nothing integrated → Unknown.
  return { status: 'unknown', remarkKind: null };
};

/** Resolve integration_data into a source-based availability view. Never throws. */
export const deriveAvailability = (raw: any, cfg: AvailConfig = DEFAULT_AVAIL_CONFIG): AvailabilityView => {
  const dmsArr: any[] = Array.isArray(raw?.dms_data) ? raw.dms_data : [];
  const dmsEntries = dmsArr.filter((e: any) => e && !e._truncated);
  // `importer_data` rows carry both stock and price; legacy payloads split them
  // into importer_inventory / importer_price.
  const importerData: any[] | null = Array.isArray(raw?.importer_data)
    ? raw.importer_data.filter((e: any) => e && typeof e === 'object' && !e._truncated) : null;
  const importerInv: any[] = importerData ?? (Array.isArray(raw?.importer_inventory) ? raw.importer_inventory : []);
  const importerPriceArr: any[] = importerData ?? (Array.isArray(raw?.importer_price) ? raw.importer_price : []);
  // Check timestamp — the payload carries it as `time` or `datetime`, as an ISO
  // string or a Unix epoch (seconds/ms). Normalized to ISO for uniform handling.
  const checkedAt: string | null = normalizeCheckedAt(raw?.time ?? raw?.datetime);

  // ── DMS grouped by line-type (original first, alternative next, others after) ──
  const buckets = new Map<string, any[]>();
  for (const e of dmsEntries) {
    const ty = dmsRowType(e);
    const arr = buckets.get(ty);
    if (arr) arr.push(e); else buckets.set(ty, [e]);
  }
  const orderedTypes = [
    ..._DMS_TYPE_ORDER.filter(t => buckets.has(t)),
    ...Array.from(buckets.keys()).filter(t => !_DMS_TYPE_ORDER.includes(t)),
  ];
  let dmsGroups: DmsTypeGroup[] = orderedTypes.map((ty) => {
    const rows = buckets.get(ty)!;
    const price = rows.map((e: any) => parsePrice(e.price)).find((p: number | null) => p !== null) ?? null;
    const locations: AvailLocation[] = rows.map((e: any) => {
      // `location` is the DMS key; `location_alias` is the human display name.
      const label = (e.location_alias && String(e.location_alias).trim())
        ? String(e.location_alias)
        : (e.location && String(e.location).trim())
          ? String(e.location)
          : String(e.extra_data?.Bodega || e.extra_data?.['Ubicación'] || '');
      const key = (e.location != null && String(e.location).trim()) ? String(e.location).trim() : label;
      return { label, qty: parseQty(e.stock_qty), key, price: parsePrice(e.price) };
    });
    const known = locations.map(l => l.qty).filter((q): q is number => q !== null);
    const totalQty = known.length ? known.reduce((a, b) => a + b, 0) : null;
    const skuRow = rows.map((e: any) => e.sku ?? e.extra_data?.SKU ?? e.extra_data?._sku).find((x: any) => x != null && x !== '') ?? null;
    const group: DmsTypeGroup = { type: ty, price, totalQty, locations, sku: skuRow != null ? String(skuRow) : null };
    // The default warehouse (org policy + member home) prices the line-type; the
    // first priced row stays the fallback when that warehouse carries no price.
    // 'list_order' (legacy) leaves the group exactly as before: price = first
    // priced row, no default warehouse, no home flags.
    const policy = cfg.locationPriority ?? 'list_order';
    if (policy === 'list_order') return group;
    const def = resolveDefaultLocation(locationOptions(group), policy, cfg.homeLocation);
    group.defaultKey = def.location?.key ?? null;
    group.defaultReason = def.reason;
    group.homeKeys = homeKeysOf(cfg.homeLocation);
    group.homeLabel = cfg.homeLabel?.trim() || null;
    group.homeStatus = def.homeStatus;
    group.price = def.location?.price ?? price;
    return group;
  });
  // Org config: hide the aftermarket line-type when disabled.
  if (!cfg.showAlternative) dmsGroups = dmsGroups.filter(g => g.type !== 'aftermarket');
  const hasDms = dmsGroups.length > 0;
  // Line-types reflect ONLY what the DMS actually returned — we do NOT synthesize
  // an empty "alternative" group. A part with a single line-type shows a static
  // tag (no switcher, no "+1"); the switcher appears only when the DMS genuinely
  // returns two or more line-types. Keep original before alternative for a stable
  // order when both are present.
  dmsGroups.sort((a, b) => {
    const ra = _DMS_TYPE_ORDER.indexOf(a.type), rb = _DMS_TYPE_ORDER.indexOf(b.type);
    return (ra < 0 ? 99 : ra) - (rb < 0 ? 99 : rb);
  });

  // ── Importer — supersession-aware qty; field-presence gates the source ──
  // importer_data has one row per importer warehouse (stock summed, first priced
  // row prices it); legacy importer_inventory only ever read its first row.
  const invEntry = importerInv[0];
  const importerRows: any[] = importerData ?? (invEntry ? [invEntry] : []);
  const rowSuperSession = (e: any) => e.supersession ?? e.super_session ?? null;
  const rowQty = (e: any) => parseQty(rowSuperSession(e) ? (e.supersession_qty ?? e.super_session_qty) : e.stock_qty);
  const superSession = importerRows.map(rowSuperSession).find((x: any) => x) ?? null;
  const knownImporterQtys = importerRows.map(rowQty).filter((q): q is number => q !== null);
  const importerQty = knownImporterQtys.length ? knownImporterQtys.reduce((a, b) => a + b, 0) : null;
  const importerPrice = importerData
    ? (importerRows.map((e: any) => parsePrice(e.price)).find((p: number | null) => p !== null) ?? null)
    : importerPriceArr[0] ? parsePrice(importerPriceArr[0].price)
      : (invEntry ? parsePrice(invEntry.price) : null);
  // Per-warehouse importer stock — only rows naming a location; a row without
  // one is the importer as a whole (the single "Importer" line). `location: -1`
  // is the workflow's price-only placeholder (no stock rows) — not a warehouse.
  const importerLocations: AvailLocation[] = importerRows
    .filter((e: any) => String(e.location) !== '-1' && String(e.location ?? e.location_alias ?? '').trim())
    .map((e: any) => {
      const key = String(e.location ?? e.location_alias).trim();
      const alias = e.location_alias != null ? String(e.location_alias).trim() : '';
      return { label: alias || key, qty: rowQty(e), key, price: parsePrice(e.price) };
    });
  const importerInStock: boolean | null =
    importerQty === null ? null : importerQty > 0;
  // Importer counts as a real availability SOURCE only when it carries usable
  // data — a price or a stock qty.
  const importerHasData = importerPrice !== null || importerQty !== null;

  // ── Source presence (spec P1/P2 null-vs-empty distinction) ──
  // dms_data: null/absent → missing; array present but no usable rows → empty; else present.
  const dmsPresence: SourcePresence =
    raw?.dms_data == null ? 'missing' : (dmsEntries.length === 0 ? 'empty' : 'present');
  // importer: neither field present → missing; present but no usable data → empty; else present.
  const importerAnyFieldPresent = raw?.importer_data != null || raw?.importer_inventory != null || raw?.importer_price != null;
  const importerPresence: SourcePresence =
    !importerAnyFieldPresent ? 'missing' : (importerHasData ? 'present' : 'empty');
  const importerFieldPresent = importerPresence !== 'missing';

  // ── Importer AS the OE line-type ──────────────────────────────────────────
  // The importer stocks OE parts. When the DMS returned rows but none for the OE
  // ('original') line-type, expose the importer as a selectable OE option so the
  // cart shows OE (from the importer) alongside the DMS alternative. It is priced
  // from the importer; its stock stays on the importer fields (importerInStock /
  // importerQty) so the OE chip reads "In Stock · Importer" and the ⓘ shows the
  // importer row. A real 'original' DMS row keeps its own group (importer stays a
  // fallback for it); importer-only parts (no DMS rows) are handled by the
  // 'importer' source — so we synthesize ONLY when DMS rows exist without an OE type.
  if (importerHasData && hasDms && !dmsGroups.some(g => g.type === 'genuine')) {
    dmsGroups.push({ type: 'genuine', price: importerPrice, totalQty: null, locations: [], sku: null });
    dmsGroups.sort((a, b) => {
      const ra = _DMS_TYPE_ORDER.indexOf(a.type), rb = _DMS_TYPE_ORDER.indexOf(b.type);
      return (ra < 0 ? 99 : ra) - (rb < 0 ? 99 : rb);
    });
  }

  // ── Supplier SKU (best-effort): DMS SKU, else importer supersession / OE ──
  const firstDms = dmsEntries[0];
  const skuRaw = firstDms?.sku ?? firstDms?.extra_data?.SKU ?? firstDms?.extra_data?._sku
    ?? superSession ?? importerPriceArr[0]?.part_oe ?? null;
  const supplierSku = skuRaw != null && skuRaw !== '' ? String(skuRaw) : null;

  // ── Source (org-configurable priority) drives DISPLAY + primary PRICE ──
  // OE-in-stock-via-importer lets the default selection prefer OE even when its
  // stock comes from the importer rather than a DMS warehouse.
  const selectedGroup = selectDmsGroup(dmsGroups, cfg.pricePriority, importerInStock === true);
  let source: AvailSource;
  switch (cfg.priority) {
    case 'importer_first': source = importerHasData ? 'importer' : hasDms ? 'dms' : 'none'; break;
    case 'dms_only':       source = hasDms ? 'dms' : 'none'; break;
    case 'importer_only':  source = importerHasData ? 'importer' : 'none'; break;
    case 'dms_first':
    default:               source = hasDms ? 'dms' : importerHasData ? 'importer' : 'none'; break;
  }
  const price: number | null = source === 'dms'
    ? (selectedGroup?.price ?? importerPrice)
    : source === 'importer' ? importerPrice : null;
  const selectedType: string | null = source === 'dms' ? (selectedGroup?.type ?? null) : null;

  // ── Status — spec cascade via the shared resolver, applied to the default
  // selected line-type so the view-level status matches the per-line chip. Org
  // 'dms_only' / 'importer_only' restrict which sources participate. ──
  const useRetailer = cfg.priority !== 'importer_only';
  const useImporter = cfg.priority !== 'dms_only';
  const { status, remarkKind } = resolveChipStatus({
    dmsPresence: useRetailer ? dmsPresence : 'missing',
    activeQty: useRetailer ? (selectedGroup?.totalQty ?? null) : null,
    importerPresence: useImporter ? importerPresence : 'missing',
    importerInStock: useImporter ? importerInStock : null,
  });

  // Winning warehouse name for the "In Stock · {warehouse}" remark — only when a
  // SINGLE named warehouse holds the stock.
  const inStockNames = Array.from(new Set(
    dmsGroups.flatMap(g => g.locations).filter(l => (l.qty ?? 0) > 0).map(l => l.label).filter(Boolean)
  ));
  const remarkWarehouse = remarkKind === 'warehouse' && inStockNames.length === 1 ? inStockNames[0] : null;

  return {
    source, status, remarkKind, remarkWarehouse, supplierSku, price, dmsGroups,
    importerPrice, importerInStock, importerQty, importerLocations, importerFieldPresent,
    dmsPresence, importerPresence, selectedType, checkedAt,
  };
};

/** Price advertised by integration_data, or null when the field is absent or
 *  carries no usable price.
 *
 *  `numberFormat` normalizes dot-format quirks so the return is ALWAYS the real
 *  amount: the importer price arrives full/real (13602) and passes through
 *  untouched, while a DMS price may arrive as a misparsed dot-thousands decimal
 *  (7.817 = 7,817) OR already-real (693975) — toRealDotAmount recovers the
 *  former and leaves the latter alone. Comma-format orgs never scale. */
export const getIntegrationPrice = (
  item: any,
  cfg: AvailConfig = DEFAULT_AVAIL_CONFIG,
  numberFormat: string = 'comma',
): number | null => {
  const raw = getIntegrationData(item);
  if (raw === undefined) return null;
  const view = deriveAvailability(raw, cfg);
  const price = view.price;
  if (price == null || numberFormat !== 'dot') return price;
  if (view.importerPrice != null && price === view.importerPrice) return price;
  return toRealDotAmount(price);
};

/** Format a check timestamp as a short relative label ("just now", "5m ago",
 *  "2h ago", "3d ago"). `nowMs` is injectable for testing. Returns null when the
 *  timestamp is missing or unparseable. Future timestamps clamp to "just now". */
export const formatCheckedAt = (checkedAt: string | number | null | undefined, nowMs: number = Date.now()): string | null => {
  const then = _toEpochMs(checkedAt);
  if (then === null) return null;
  const diffSec = Math.round((nowMs - then) / 1000);
  if (diffSec < 45) return 'just now';
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
};
